import "@/index.css";
import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { useLenis } from "@/components/motion";
import RouteChangeTracker from "@/components/Analytics";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Solutions from "@/pages/Solutions";
import Industries from "@/pages/Industries";
import IndustryDetail from "@/pages/IndustryDetail";
import Portfolio from "@/pages/Portfolio";
import ProjectDetail from "@/pages/ProjectDetail";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Careers from "@/pages/Careers";
import ContactPage from "@/pages/ContactPage";
import Legal from "@/pages/Legal";
import Sitemap from "@/pages/Sitemap";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import NotFound from "@/pages/NotFound";
import Norbit from "@/pages/Norbit";
import Reach from "@/pages/Reach";
import Guardian from "@/pages/Guardian";
import WebDevelopment from "@/pages/WebDevelopment";
import SoftwareDevelopment from "@/pages/SoftwareDevelopment";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("ErrorBoundary caught an unhandled render error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0B0B0B] text-white flex flex-col items-center justify-center p-6 text-center">
          <div className="w-12 h-12 rounded-2xl bg-[#FF5A1F]/15 border border-[#FF5A1F]/30 flex items-center justify-center text-[#FF5A1F] mb-4 text-xl font-bold">
            !
          </div>
          <h2 className="text-2xl font-bold mb-2 text-white">Something went wrong</h2>
          <p className="text-neutral-400 max-w-md mb-6 text-sm leading-relaxed">
            We encountered an unexpected issue while loading this page. Please try refreshing or returning to the home page.
          </p>
          <a
            href="/"
            className="px-6 py-2.5 rounded-full bg-[#FF5A1F] text-white font-semibold text-sm hover:bg-[#ff6d38] transition-all shadow-[0_4px_18px_rgba(255,90,31,0.35)]"
          >
            Return to Home
          </a>
        </div>
      );
    }
    return this.props.children;
  }
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  useLenis();
  return (
    <AuthProvider>
      <Toaster theme="light" position="bottom-right" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <RouteChangeTracker />
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/norbit" element={<Norbit />} />
            <Route path="/reach" element={<Reach />} />
            <Route path="/guardian" element={<Guardian />} />
            <Route path="/services/cybersecurity" element={<Guardian />} />
            <Route path="/services/technology-consultancy" element={<Navigate to="/services/consultancy" replace />} />
            <Route path="/services/iot-automation" element={<Navigate to="/services/iot-solutions" replace />} />
            <Route path="/services/erp-solutions" element={<Navigate to="/services/erp" replace />} />
            <Route path="/services/mobile-app-development" element={<Navigate to="/services/mobile-apps" replace />} />
            <Route path="/services/custom-software" element={<Navigate to="/services/software-development" replace />} />
            <Route path="/services/software-development" element={<SoftwareDevelopment />} />
            <Route path="/services/web-development" element={<WebDevelopment />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/solutions" element={<Solutions />} />
            <Route path="/industries" element={<Industries />} />
            <Route path="/industries/:slug" element={<IndustryDetail />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/portfolio/:slug" element={<ProjectDetail />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/legal/:type" element={<Legal />} />
            <Route path="/sitemap" element={<Sitemap />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
